/**
 * The route table (extracted so it is unit-testable). URL paths are ENGLISH code
 * (AX-9); German lives only in nav link TEXT.
 *
 * ROOT DEFECT FIX: a full-path parent record wins over its "" child (documented
 * router precedence), so a bare "/" with only a "" child renders a SILENT EMPTY
 * OUTLET. The fix (web-kit demo pattern): a dedicated redirect record for "/",
 * declared BEFORE the children-bearing "/", plus NO "" child.
 */

import type { RouteRecord, Urn } from "@platform/web-kit"
import type { ShellSession } from "./domain"
import { SETTINGS_GATE_ROLE } from "./domain"

const opportunityUrn = (id: string): Urn => `urn:career:opportunity:${id}`
const applicationUrn = (id: string): Urn => `urn:career:application:${id}`

export function buildRoutes(session: ShellSession): RouteRecord[] {
  const isAuthed = (): boolean => session.user !== null || !session.authEnforced

  const devRoute: RouteRecord = { pattern: "dev/heartbeat", surface: "career-heartbeat" }

  const shellChildren: RouteRecord[] = [
    { pattern: "onboarding", surface: "career-onboarding" },
    { pattern: "onboarding/review", surface: "career-extraction-review" },
    { pattern: "workshops", surface: "career-workshop-list" },
    // The runner rebinds on slug change (KitElement urn rebind) — the slug is the
    // English workshop kind (self-knowledge | search-profile | profiler).
    { pattern: "workshops/:slug", surface: "career-workshop-runner", urn: ({ slug }): Urn => `urn:career:workshop:${slug ?? ""}` },
    { pattern: "compass", surface: "career-compass-hub" },
    { pattern: "compass/profile", surface: "career-profile" },
    { pattern: "compass/cv", surface: "career-cv-workbench" },
    { pattern: "compass/evidence", surface: "career-evidence-ledger" },
    { pattern: "compass/assets", surface: "career-assets" },
    { pattern: "search", surface: "career-search" },
    { pattern: "search/opportunities/:id", surface: "career-opportunity-detail", urn: ({ id }): Urn => opportunityUrn(id ?? "") },
    { pattern: "applications", surface: "career-applications" },
    { pattern: "applications/:id", surface: "career-application-review", urn: ({ id }): Urn => applicationUrn(id ?? "") },
    { pattern: "runs", surface: "career-runs" },
    { pattern: "credits", surface: "career-credits" },
    { pattern: "settings/ai", surface: "career-llm-settings" },
    ...(session.devMode ? [devRoute] : []),
    { pattern: "*", surface: "career-not-found" }, // in-shell fallback, LAST
  ]

  return [
    { pattern: "/login", surface: "career-login" },
    // Print view — a TOP-LEVEL route (outside the shell) so no nav chrome renders and
    // @page print CSS owns the page. Reads ?version= from the query string.
    { pattern: "/cv-print", surface: "career-cv-print" },
    // Bare "/" → /search. A dedicated leaf record BEFORE the children-bearing "/"
    // (which would otherwise win as a full-path parent and mount an empty outlet).
    { pattern: "/", surface: "career-app", guard: async () => ({ kind: "redirect", url: "/search" }) },
    {
      pattern: "/",
      surface: "career-app",
      guard: async () => (isAuthed() ? { kind: "allow" } : { kind: "redirect", url: "/login" }),
      children: shellChildren,
    },
  ]
}

/** The role that ungates LLM settings — re-exported for the shell nav. */
export { SETTINGS_GATE_ROLE }
